import type { ReactNode } from 'react'
import { Box } from '@mui/material'

interface StickyActionBarProps {
  /** Left-hand side: what the actions are about — a count, a status, an outstanding-issue line. */
  status: ReactNode
  /** The actions themselves, right-aligned. */
  children: ReactNode
  testId?: string
}

/**
 * Persistent bottom action bar, so committing stays reachable without scrolling back up a long page.
 *
 * Deliberately holds no opinion about what it commits: the status side and the buttons both come
 * from the consumer, because the two screens using it commit different things ("update care teams",
 * "save draft") and each owns its own wording and disabled rules.
 */
const StickyActionBar = ({ status, children, testId }: StickyActionBarProps) => (
  <Box
    sx={{
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      mx: -3,
      mt: 1,
      px: 3,
      py: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      bgcolor: 'background.paper',
      borderTop: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
    }}
    data-testid={testId}
  >
    <Box sx={{ minWidth: 0, mr: 'auto' }}>{status}</Box>
    {children}
  </Box>
)

export default StickyActionBar
